import { Head, Link } from '@inertiajs/react'
import { Download, Clock, HardDrive, Database, FolderArchive, AlertCircle, CheckCircle } from 'lucide-react'
import Heading from '@/components/heading'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import AppLayout from '@/layouts/app-layout'
import { serversPath, appPath, appBackupsPath, appBackupPath, downloadAppBackupPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'
import type { App, Backup, BackupConfig } from '@/types'

type BackupShowProps = {
  backup: Backup & {
    app: Pick<App, 'id' | 'name'>
    backup_config: Pick<BackupConfig, 'id' | 'backup_type' | 'destination_type'>
    duration_formatted: string | null
    file_size_formatted: string | null
  }
}

export default function BackupShow({ backup }: BackupShowProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Servers', href: serversPath() },
    { title: backup.app.name, href: appPath(backup.app.id) },
    { title: 'Backups', href: appBackupsPath(backup.app.id) },
    { title: `Backup #${backup.id}`, href: appBackupPath(backup.app.id, backup.id) },
  ]

  function getStatusIcon(status: string) {
    switch (status) {
      case 'success': return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'error': return <AlertCircle className="h-5 w-5 text-destructive" />
      default: return <Clock className="h-5 w-5 text-muted-foreground" />
    }
  }

  function getStatusVariant(status: string) {
    switch (status) {
      case 'success': return 'default'
      case 'error': return 'destructive'
      case 'running': return 'outline'
      default: return 'secondary'
    }
  }

  function formatDate(date: string | null | undefined): string {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Backup #${backup.id}`} />

      <div className="p-6">
        <div className="flex items-center justify-between">
        <Heading
          title={`Backup #${backup.id}`}
          description={`${backup.backup_config.backup_type} backup - ${backup.frequency_type}`}
        />
        {backup.status === 'success' && backup.file_path && (
          <Button asChild>
            <a href={downloadAppBackupPath(backup.app.id, backup.id)}>
              <Download className="mr-2 h-4 w-4" />
              Download
            </a>
          </Button>
        )}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {getStatusIcon(backup.status)}
              Backup Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              <Badge variant={getStatusVariant(backup.status)}>
                {backup.status}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Type</span>
              <span className="flex items-center gap-2">
                {backup.backup_config.backup_type === 'database' ? (
                  <Database className="h-4 w-4" />
                ) : (
                  <FolderArchive className="h-4 w-4" />
                )}
                {backup.backup_config.backup_type}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Frequency</span>
              <span className="capitalize">{backup.frequency_type}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Destination</span>
              <span className="capitalize">{backup.backup_config.destination_type}</span>
            </div>
          </CardContent>
        </Card>

        {/* Details Card */}
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Started At</span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatDate(backup.started_at)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Completed At</span>
              <span>{formatDate(backup.completed_at)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Duration</span>
              <span>{backup.duration_formatted || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">File Size</span>
              <span className="flex items-center gap-1">
                <HardDrive className="h-4 w-4" />
                {backup.file_size_formatted || 'N/A'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* File Path */}
        {backup.file_path && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>File Location</CardTitle>
            </CardHeader>
            <CardContent>
              <code className="block rounded bg-muted p-3 font-mono text-sm">
                {backup.file_path}
              </code>
            </CardContent>
          </Card>
        )}

        {/* Error Message */}
        {backup.error_message && (
          <Card className="lg:col-span-2 border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">Error</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded bg-destructive/10 p-3 text-destructive">
                {backup.error_message}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Log */}
        {backup.log && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Backup Log</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="max-h-96 overflow-auto rounded bg-muted p-4 font-mono text-xs">
                {backup.log}
              </pre>
            </CardContent>
          </Card>
        )}
      </div>
      </div>
    </AppLayout>
  )
}
